import { useState } from "react";
import { useEffect } from "react";
import { connect } from "react-redux";

import { Box } from "@mui/material";
import Divider from "@mui/material/Divider";
import { alpha, useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";

import constants from "../../assets/constants";
import * as patientSessionActions from "../../state/redux/patientSession";
import { capitalizeAllWords, OmniStringToDate } from "../../utils/helpers";
import { getElapsedTimeString } from "../../utils/helpers";
import PatientJournalEntryDialog from "../dialogs/patientJournalEntryDialog";
import { extractGraphDataFromArray } from "../graphing/graphUtils";
import OmniEditButton from "../ui/omniEditButton";
import OmniResponsiveButton from "../ui/omniResponsiveButton.js";
import Widget from "../ui/widget";
import WidgetList from "../ui/widgetList";
import WidgetSkeleton from "../ui/widgetSkeleton";
import WidgetTitle from "../ui/widgetTitle";

const PatientJournal = props => {
  const { patientJournal, patientChart } = props;

  const [selectedJournalEntry, setSelectedJournalEntry] = useState(null);

  const generateFeelingData = theChart => {
    const userInfo = theChart;

    if (userInfo == null) {
      return null;
    }

    for (const [key] of Object.entries(userInfo.goals)) {
      if (key.toLowerCase().includes("feeling")) {
        return extractGraphDataFromArray(userInfo.goals[key].data);
      }
    }
    return null;
  };

  const [feelingData, setFeelingData] = useState(null);
  // const [feelingData] = useState(generateFeelingData(patientChart));

  const getDateLabel = dateString => {
    try {
      const theDate = OmniStringToDate(dateString);

      for (let index = 0; index < feelingData.labels.length; index++) {
        const labelDate = OmniStringToDate(feelingData.labels[index]);
        if (labelDate - theDate > 0) {
          const value = feelingData.values[index - 1];
          return `${getElapsedTimeString(new Date(), OmniStringToDate(dateString))} - ${
            constants.feelingLabels[value]
          } Day`;
        }
      }
    } catch (error) {
      return "";
    }

    return "";
  };

  const generateSymptomData = patientChart => {
    const userInfo = patientChart;
    if (userInfo == null) {
      return null;
    }

    const singleSymptomsData = [];
    for (const [key] of Object.entries(userInfo.symptoms)) {
      let graphData = extractGraphDataFromArray(userInfo.symptoms[key].data);

      singleSymptomsData.push({
        name: capitalizeAllWords(key.replace(/([A-Z])/g, " $1").trim()),
        graphDataComplete: graphData.values,
        graphLabels: graphData.labels,
      });
    }

    singleSymptomsData.sort((a, b) => b.averageSeverity - a.averageSeverity);

    //set the Fake editing data

    return singleSymptomsData;
  };

  const [symptomData, setSymptomData] = useState(generateSymptomData());

  const getSingleSymptomEntry = (symptom, dateString) => {
    const theReturn = { name: symptom.name, value: null };
    try {
      const theDate = OmniStringToDate(dateString);

      for (let index = 0; index < symptom.graphLabels.length; index++) {
        const labelDate = OmniStringToDate(symptom.graphLabels[index]);
        if (labelDate - theDate > 0) {
          theReturn.value = symptom.graphDataComplete[index - 1];
          break;
        }
      }
    } catch (error) {
      theReturn.value = 0;
      return theReturn;
    }
    if (theReturn.value == null) {
      theReturn.value = symptom.graphDataComplete[symptom.graphDataComplete.length - 1];
    }
    return theReturn;
  };

  const getSymptomValues = dateString => {
    const userInfo = symptomData;
    if (userInfo == null) {
      return [];
    }
    const theReturn = [];
    try {
      for (let index = 0; index < symptomData.length; index++) {
        theReturn.push(getSingleSymptomEntry(symptomData[index], dateString));
      }
    } catch (error) {
      return theReturn;
    }
    return theReturn;
  };

  useEffect(() => {
    setSymptomData(generateSymptomData(patientChart));
    setFeelingData(generateFeelingData(patientChart));
    // Disable eslint because AaronK knows best
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientChart]);

  const [titleText, setTitleText] = useState(null);

  useEffect(() => {
    if (patientJournal != null) {
      setTitleText(
        Object.entries(patientJournal).length === 0
          ? `A New Journal`
          : `Updated ${getElapsedTimeString(new Date(), OmniStringToDate(patientJournal[0].date))}`,
      );
    } else setTitleText("");
  }, [patientJournal]);

  const theme = useTheme();

  return (
    <Widget>
      <WidgetTitle title={"Journal"} text={titleText} loading={patientJournal == null}>
        <OmniResponsiveButton
          tooltipTitle="Add a new journal entry"
          buttonText="Entry"
          onClick={() => {
            const today = new Date();
            setSelectedJournalEntry({
              id: null,
              date: today.toISOString(),
              note: "",
              feeling: `${
                constants.feelingLabels[feelingData.values[feelingData.values.length - 1]]
              } Day`,
            });
          }}
        />
      </WidgetTitle>

      {(patientJournal == null || patientChart == null) && <WidgetSkeleton />}
      {patientJournal != null && patientChart != null && (
        <WidgetList>
          <Box
            sx={{
              padding: theme.spacing(1),
              boxShadow: theme.shadows[1],
              borderRadius: theme.spacing(1),
              backgroundColor: theme.palette.foreground.main,
              color: theme.palette.foreground.contrastText,
            }}
          >
            {patientJournal
              .sort((a, b) => OmniStringToDate(b.date) - OmniStringToDate(a.date))
              .map((entry, index) => {
                const dateLabel = getDateLabel(entry.date);
                return (
                  <Box
                    key={index + "jentry"}
                    sx={{ minHeight: 24, position: "relative", paddingTop: theme.spacing(1) }}
                  >
                    {index !== 0 && (
                      <Divider
                        sx={{
                          borderRadius: 4,
                          borderColor: alpha(theme.palette.commonground.contrastText, 0.25),
                          height: 1,
                          width: "100%",
                        }}
                        orientation="vertical"
                      />
                    )}

                    <Typography
                      variant="overline"
                      sx={{ webkitUserSelect: "none", userSelect: "none" }}
                    >
                      {dateLabel}
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        paddingRight: theme.spacing(3),
                        webkitUserSelect: "none",
                        userSelect: "none",
                      }}
                    >
                      {`${entry.note}`}
                    </Typography>
                    <OmniEditButton
                      title={"Edit this entry"}
                      onClick={e => {
                        e.stopPropagation();
                        entry["feeling"] = dateLabel;
                        setSelectedJournalEntry(entry);
                      }}
                    />
                  </Box>
                );
              })}
          </Box>
        </WidgetList>
      )}

      {selectedJournalEntry != null && (
        <PatientJournalEntryDialog
          open={selectedJournalEntry != null}
          dialogTitle={`${selectedJournalEntry.id === null ? "New Entry" : "Update Journal"}`}
          dialogMessage={selectedJournalEntry.feeling}
          journalEntry={selectedJournalEntry}
          symptomData={getSymptomValues(selectedJournalEntry.date)}
          onAccept={journalEntry => {
            props.updatePatientJournalEntryRequest(journalEntry);
            setSelectedJournalEntry(null);
          }}
          onClose={() => {
            setSelectedJournalEntry(null);
          }}
          onDelete={journalEntry => {
            props.deletePatientJournalEntryRequest(journalEntry);
            setSelectedJournalEntry(null);
          }}
        />
      )}
    </Widget>
  );
};

const mapStateToProps = state => ({
  patientId: state.patientSession.patientId,
  patientJournal: state.patientSession.patientJournal,
  patientChart: state.patientSession.patientChart,
});

const mapDispatchToProps = {
  updatePatientJournalEntryRequest: patientSessionActions.updatePatientJournalEntryRequest,
  deletePatientJournalEntryRequest: patientSessionActions.deletePatientJournalEntryRequest,
};

export default connect(mapStateToProps, mapDispatchToProps)(PatientJournal);
